import React, { useMemo } from 'react';

import { SearchIcon } from '@vlabs/icons';
import { GroupedCheckbox } from '@vlabs/monokit/shared/ui/grouped-checkbox';
import { Button } from '@vlabs/uikit/components/controls/button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { useTranslation } from 'react-i18next';

import { AddedSourcesGroup } from './AddedSourcesGroup';
import { NoSourceGroup } from './NoSourceGroup';
import st from './SourceGroupedCheckbox.module.sass';
import { useLunaStreamsUiGroups } from '../hooks/useLunaStreamsUiGroups';
import { useSourceSearch } from '../hooks/useSourceSearch';
import { useVLAccessSources } from '../hooks/useVLAccessSources';

interface SourceGroupedCheckboxProps {
  selectedSources: string[];
  onChangeSelectedSource: (next: string[]) => void;
  customSourceNames: string[];
  onChangeCustomSourceNames: (next: string[]) => void;
  testId?: string;
}

export function SourceGroupedCheckbox({
  selectedSources,
  onChangeSelectedSource,
  customSourceNames,
  onChangeCustomSourceNames,
  testId = 'SourceGroupedCheckbox',
}: SourceGroupedCheckboxProps): React.JSX.Element {
  const { t } = useTranslation();
  const { groups, streams, uiGroups } = useLunaStreamsUiGroups();
  const { uiVlAccessGroup } = useVLAccessSources();

  const allUiGroups = useMemo(() => {
    return [
      ...uiGroups,
      ...(uiVlAccessGroup ? [uiVlAccessGroup] : []),
    ];
  }, [uiGroups, uiVlAccessGroup]);

  const {
    searchValue,
    setSearchValue,
    visibleUiGroups,
    visibleCustomSourceNames,
    canAdd,
    onAdd,
  } = useSourceSearch({
    uiGroups: allUiGroups,
    streams,
    groups,
    selectedSources,
    customSourceNames,
    onChangeSelectedSource,
    onChangeCustomSourceNames,
  });

  return (
    <div
      className={st.Content}
      data-testid={testId}
    >
      <div className={st.Toolbar}>
        <Input
          className={st.SearchInput}
          icon={<SearchIcon />}
          id="source_search"
          isClearable
          name="source_search"
          onChange={(e) => setSearchValue(e.target.value)}
          onClear={() => setSearchValue('')}
          size="small"
          value={searchValue}
        />
        <Button
          disabled={!canAdd}
          onClick={onAdd}
          size="small"
          variant="flat"
        >
          {t('кнопка.добавить')}
        </Button>
      </div>

      <div className={st.ScrollArea}>
        <div className={st.Sections}>
          <AddedSourcesGroup
            customSourceNames={customSourceNames}
            onChangeCustomSourceNames={onChangeCustomSourceNames}
            onChangeSelectedSource={onChangeSelectedSource}
            selectedSources={selectedSources}
            visibleCustomSourceNames={visibleCustomSourceNames}
          />

          <div className={st.GroupsList}>
            {visibleUiGroups.map((group) => (
              <GroupedCheckbox
                group={group}
                key={group.id}
                onChange={onChangeSelectedSource}
                value={selectedSources}
              />
            ))}
          </div>

          {!searchValue.trim() && (
            <NoSourceGroup
              onChangeSelectedSource={onChangeSelectedSource}
              selectedSources={selectedSources}
            />
          )}
        </div>
      </div>

      <Button
        kind="negative"
        onClick={() => onChangeSelectedSource([])}
        size="small"
        variant="filled"
      >
        {t('button.clear')}
      </Button>
    </div>
  );
}
